"use client";

// Deep import, not the barrel: `@repo/common` re-exports the AWS SDK clients,
// which resolve to ESM under the browser export condition and break any jsdom
// component test that renders a card.
import {
  formatPayCardDisplay,
  joinerFigure,
  type PayCardDisplay,
} from "@repo/common/opportunity-pay";
import { keepPreviousData } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useMemo } from "react";

import { advertisedRatesInput } from "@/lib/advertised-rates-query";
import { resolvedPayInput } from "@/lib/opportunity-utils";
import { formatRateEndsAt } from "@/lib/payout-format";
import { api } from "@/lib/trpc/react";
import { cn } from "@/lib/utils";

/** The listing fields a figure is formatted against; a card passes itself. */
export interface AdvertisedPayListing {
  payRateFrequency: string;
  estimatedDurationMins?: number | null;
  category?: string | null;
}

/** Mirrors the procedure's output: the deep-import note above covers `@repo/db`. */
export interface AdvertisedFigure {
  rateUsd: string;
  payRateFrequency: "ONE_TIME" | "HOURLY" | "PER_TASK";
  /**
   * Set whenever this figure is a running temporary rate, on either leg: the
   * viewer's own carve-out, or one the study itself is running.
   *
   * A temporary figure shown bare reads as a raise, and an expert told their
   * rate went up when it goes back down next week was told the wrong thing
   * (decisions 38 and 40). Every surface that prints the figure prints the end
   * instant beside it; a detail surface also strikes through the standing rate
   * it displaced, which is what `standingRateUsd` is for and which is null
   * where no standing rate stands behind it on whichever leg answered.
   */
  temporary?: { until: string | Date; standingRateUsd: string | null };
}

export interface AdvertisedRate extends AdvertisedFigure {
  /** Set only where the figure above is the viewer's own row. */
  joinerRate?: AdvertisedFigure;
}

/** `pending` keeps a placeholder, never the listing columns, on a study not yet answered. */
export interface AdvertisedRates {
  byStudy: ReadonlyMap<string, AdvertisedRate>;
  pending: boolean;
}

const NO_RATES: AdvertisedRates = { byStudy: new Map(), pending: false };

/**
 * One study's resolved figure, for the viewer, out of a page the caller holds.
 *
 * A study id is nullable on nearly every listing shape, and `undefined` is
 * already the answer for a study with nothing to resolve, so the two collapse
 * here rather than at each call site.
 */
export function advertisedRateOf(
  rates: AdvertisedRates,
  studyId: string | null | undefined,
): AdvertisedRate | undefined {
  return studyId ? rates.byStudy.get(studyId) : undefined;
}

/** What a referral estimate is a share of: never the viewer's own row. */
export function joinerRateOf(
  rates: AdvertisedRates,
  studyId: string | null | undefined,
): AdvertisedFigure | undefined {
  return joinerFigure(advertisedRateOf(rates, studyId)) ?? undefined;
}

/** Whether this study's figure is still on its way, so a placeholder stands in. */
export function isAdvertisedRatePending(
  rates: AdvertisedRates,
  studyId: string | null | undefined,
): boolean {
  return Boolean(studyId) && rates.pending && !rates.byStudy.has(studyId ?? "");
}

const AdvertisedRatesContext = createContext<AdvertisedRates>(NO_RATES);

function toRates(
  data: readonly (AdvertisedRate & { studyId: string })[] | undefined | null,
  pending: boolean,
): AdvertisedRates {
  const byStudy = new Map<string, AdvertisedRate>();
  for (const { studyId, ...rate } of data ?? []) {
    byStudy.set(studyId, rate);
  }
  return { byStudy, pending };
}

/**
 * Every study on a page, resolved in one request.
 *
 * Call it once per page. A page draws dozens of pay figures and tRPC's link
 * batching merges the HTTP requests, not the database work, so a card asking
 * for its own rate would still cost one resolver pass per card.
 *
 * The previous answer is held while a wider page of ids is fetched, so a study
 * already on screen keeps its figure while the ones joining it load.
 */
export function useAdvertisedRates(
  studyIds: (string | null | undefined)[],
): AdvertisedRates {
  // The array identity changes on every render of a caller that builds it
  // inline; the ids themselves are what the query is keyed on, so they are what
  // the memo hangs off.
  const idsKey = advertisedRatesInput(studyIds).studyIds.join(",");
  const input = useMemo(
    () => ({ studyIds: idsKey ? idsKey.split(",") : [] }),
    [idsKey],
  );

  const { data, isLoading, isPlaceholderData } =
    api.opportunityListing.getAdvertisedRates.useQuery(input, {
      enabled: input.studyIds.length > 0,
      staleTime: 1000 * 60,
      placeholderData: keepPreviousData,
    });

  const pending = isLoading || isPlaceholderData;
  return useMemo(() => toRates(data, pending), [data, pending]);
}

/** For copy that leaves the app: standing (decision 21), never the viewer's own row. */
export function useOffAppRate(studyId: string | null | undefined): {
  rate: AdvertisedFigure | undefined;
  pending: boolean;
} {
  const { data, isLoading } =
    api.opportunityListing.getAdvertisedRates.useQuery(
      { studyIds: studyId ? [studyId] : [], offApp: true },
      { enabled: Boolean(studyId), staleTime: 1000 * 60 },
    );
  return {
    rate: data?.find((rate) => rate.studyId === studyId),
    pending: isLoading,
  };
}

/**
 * Publishes a page's resolved rates to the pay figures beneath it.
 *
 * Takes the rates rather than fetching them, because a page that orders its
 * cards by pay needs the same rates itself and one read should serve both.
 *
 * A surface that forgets this provider shows no figure at all rather than a
 * stale one, and `advertised-pay-provider-audit.test.ts` fails on any host of
 * `OpportunityCard` that does.
 */
export function AdvertisedRatesProvider({
  rates,
  children,
}: {
  rates: AdvertisedRates;
  children: ReactNode;
}) {
  return (
    <AdvertisedRatesContext.Provider value={rates}>
      {children}
    </AdvertisedRatesContext.Provider>
  );
}

/**
 * The figure a referral estimate is a share of, for one study under the
 * nearest provider, and whether it is still loading.
 */
export function useJoinerRate(studyId: string | null | undefined): {
  rate: AdvertisedFigure | undefined;
  pending: boolean;
} {
  const rates = useContext(AdvertisedRatesContext);
  return {
    rate: joinerRateOf(rates, studyId),
    pending: isAdvertisedRatePending(rates, studyId),
  };
}

/**
 * The pay figure for one study, or nothing.
 *
 * Deliberately not exported. A caller that holds the bare figure can put it
 * behind a `||`, and the one surface that wanted to -- a referral card, whose
 * "Earn" is a 20% commission -- would then have quoted the study's whole pay
 * wherever the commission could not be computed. `AdvertisedPay` renders the
 * figure, a placeholder while it loads, or nothing, and none of those can be
 * mistaken for another number.
 */
function useAdvertisedPay(input: {
  studyId: string | null | undefined;
  listing: AdvertisedPayListing;
}): { pay: PayCardDisplay | null; rate: AdvertisedRate | undefined } | null {
  const rates = useContext(AdvertisedRatesContext);
  if (isAdvertisedRatePending(rates, input.studyId)) {
    return null;
  }
  const rate = advertisedRateOf(rates, input.studyId);
  return {
    pay: formatPayCardDisplay(
      resolvedPayInput({ rate, listing: input.listing }),
    ),
    rate,
  };
}

/** Sized like a short figure so the card does not reflow when it lands. */
export function PayPlaceholder({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      data-testid="pay-placeholder"
      className={cn(
        "bg-primary/5 inline-block h-[1em] w-14 animate-pulse rounded-md align-middle",
        className,
      )}
    />
  );
}

/**
 * When a running temporary rate hands back, worded the same everywhere.
 *
 * One formatter rather than a date call at each site: the instant is the whole
 * of what makes the figure beside it readable as temporary, and two surfaces
 * wording it differently is two surfaces to check when the copy changes. It
 * carries the time of day and the zone because the stored bound is exclusive
 * and carries a real wall clock; see `formatRateEndsAt`.
 *
 * Returns null for anything unparseable rather than printing "Invalid Date"
 * next to an amount, which would read as a fault in the money.
 */
export function formatTemporaryUntil(
  until: string | Date | null | undefined,
): string | null {
  const endsAt = formatRateEndsAt(until);
  return endsAt === null ? null : `until ${endsAt}`;
}

/**
 * The standing rate a running temporary displaces, struck through.
 *
 * Deliberately a separate piece from the note below rather than one block. The
 * strike only says "was, and now is not" while it sits against the live figure;
 * moved onto its own line it reads as a second, cancelled price. So it is
 * rendered inside the figure line and the note is rendered under it, which is
 * the treatment the handbook card already used and the one that was lost when
 * the two were flattened onto one row.
 *
 * Renders nothing where no standing rate stands behind the carve-out, where it
 * prices in another unit (the server drops it), or where the figure above is
 * not temporary at all, so a caller places it unconditionally and does not
 * branch.
 *
 * The figure goes through the same formatter as the one beside it, so the two
 * are in the same unit and rounded the same way; a struck-through number
 * formatted differently reads as a different kind of thing.
 */
export function TemporaryStandingRate({
  rate,
  listing,
  className,
}: {
  rate: AdvertisedFigure | undefined;
  listing: AdvertisedPayListing;
  className?: string;
}) {
  const standingUsd = rate?.temporary?.standingRateUsd;
  const standing = standingUsd
    ? formatPayCardDisplay(
        resolvedPayInput({
          rate: {
            rateUsd: standingUsd,
            payRateFrequency: rate.payRateFrequency,
          },
          listing,
        }),
      )
    : null;
  if (!standing) {
    return null;
  }
  return (
    <s className={className ?? "text-muted-foreground/70 font-normal"}>
      {standing.amount}
    </s>
  );
}

/**
 * The line that says when the figure above it stops.
 *
 * No label. "$400 / hr until 21 Sep" already says the rate is temporary, and a
 * word repeating that outranked the money it was describing -- first as a
 * bordered chip, then as a tinted one, then as a coloured word. The sentence
 * was always carrying it.
 *
 * Its own line under the figure, never welded to the end of one: run together
 * with the amount, its cadence and a struck second figure, the whole thing
 * reads as one string of money nobody parses. The blue apply hero is the
 * exception and places it inline itself, because its figure is centred and a
 * line hung off the amount's left edge lines up with nothing a reader can see.
 *
 * Colour and size come from the caller: this is tertiary text and has to sit
 * below whatever sentence it shares a surface with, which is a different value
 * on a blue panel than on a card.
 *
 * Renders nothing unless the figure above it is a running temporary, so a
 * caller can place it unconditionally and not branch.
 */
export function TemporaryRateNote({
  rate,
  className,
}: {
  rate: AdvertisedFigure | undefined;
  className?: string;
}) {
  return (
    <TemporaryLabel
      until={formatTemporaryUntil(rate?.temporary?.until)}
      className={className}
    />
  );
}

/**
 * The same line, for a surface that holds the formatted instant rather than the
 * rate it came from.
 *
 * The `/me/home` rail is the one: it flattens every card to a display string
 * before it renders. It goes through this component rather than repeating the
 * markup, because a second copy is how the rail came to print the date in a
 * different shape from the card beside it.
 */
export function TemporaryLabel({
  until,
  className,
}: {
  /** Already worded, from `formatTemporaryUntil`. Nothing renders without it. */
  until: string | null | undefined;
  className?: string;
}) {
  if (!until) {
    return null;
  }
  return (
    <span
      className={cn(
        "text-muted-foreground text-[13px] leading-[1.4] font-normal",
        className,
      )}
    >
      {until}
    </span>
  );
}

export function AdvertisedPay({
  studyId,
  listing,
  className,
  amountClassName,
  suffixClassName,
  standingClassName,
  temporaryClassName,
}: {
  studyId: string | null | undefined;
  listing: AdvertisedPayListing;
  className?: string;
  amountClassName?: string;
  /** Omit to render the amount alone, where the surrounding copy states the cadence. */
  suffixClassName?: string;
  /** Size for the struck rate a temporary displaces. See `TemporaryStandingRate`. */
  standingClassName?: string;
  /** Size and colour for the end instant, which rides after the cadence. */
  temporaryClassName?: string;
}) {
  const resolved = useAdvertisedPay({ studyId, listing });
  if (!resolved) {
    return (
      <span className={className}>
        <PayPlaceholder className={amountClassName} />
      </span>
    );
  }
  const { pay, rate } = resolved;
  if (!pay) {
    return null;
  }

  // One line, all of it: the rate it reverts to, the rate now, the cadence and
  // the day it ends. A second line grew the card, and a grid row is only as
  // tall as its tallest card, so one study running a temporary rate pushed
  // every card beside it down with it.
  return (
    <span className={className}>
      <TemporaryStandingRate
        rate={rate}
        listing={listing}
        className={cn("mr-[7px] font-normal", standingClassName)}
      />
      <strong className={amountClassName}>{pay.amount}</strong>
      {pay.suffix && suffixClassName ? (
        <span className={suffixClassName}> {pay.suffix}</span>
      ) : null}
      <TemporaryRateNote
        rate={rate}
        className={cn("ml-[9px] whitespace-nowrap", temporaryClassName)}
      />
    </span>
  );
}
